// Build ReportData from the scoring profile + framework + branding.
// Pure function — no React, no @react-pdf, no docx. Both renderers
// consume the result.

import {
  getMappingsForThreat,
  THREATS as THREATS_SEED,
  THREAT_CATEGORIES as THREAT_CATEGORIES_SEED,
  FRAMEWORKS as FRAMEWORKS_SEED,
} from "../mappings";
// V4.2 — the user-facing 0–100 calibration. The report layer takes no
// ColorConfig, so it uses the default-bound calibration; if admin threshold
// overrides are ever plumbed through `PreparationInputs`, this is the one line
// that has to become `makeColorFns(colors).toScore100`.
import { toScore100 as score100 } from "../ui/utils";
import { USE_CASES } from "../taxonomy";
import { CISCO_PRODUCT_MAPPINGS } from "../framework/cisco-products";
import { formatDomain, formatMaturityLevel } from "../shared/labels";
import { siteContent } from "../shared/site-config";
import { buildExecutiveSummary, buildProductExposure, buildThreatMatrix } from "./derive";
import { getRoleActions } from "../role-actions";
import { getEntityCategory, getEntityItem, resolveUseCaseId } from "../../dataImport/catalog/entityCatalog";
import { buildTopology } from "../../dataImport/topology/buildTopology";
import { summariseTopology } from "../topology-summary";
import type {
  ControlDefinition,
  CoverageRow,
  FrameworkCoverage,
  PerUseCaseSummary,
  PreparationInputs,
  RecommendationDetail,
  ReportData,
  ThreatDefinition,
} from "./types";

// V3.3: D2 (Utilize) is now in-scope. Authored questions + threat mappings
// land in scoring, so the report surfaces all three domains.

export function prepareReportData({
  input,
  profile,
  framework,
  branding,
  focusedRecommendationIds,
  threats: threatsArg,
  threatCategories: categoriesArg,
  frameworks: frameworksArg,
  ciscoMappings: ciscoMappingsArg,
  entities: entitiesArg,
  topology: topologyArg,
  detailLevel,
}: PreparationInputs): ReportData {
  // Resolved content (promoted/overridden edits) with a static-seed fallback so
  // pre-V5 callers and the sample generator keep working unchanged.
  const THREATS = threatsArg ?? THREATS_SEED;
  const THREAT_CATEGORIES = categoriesArg ?? THREAT_CATEGORIES_SEED;
  const FRAMEWORKS = frameworksArg ?? FRAMEWORKS_SEED;
  const CISCO_MAPPINGS = ciscoMappingsArg ?? CISCO_PRODUCT_MAPPINGS;
  const flatControls = framework.domains.flatMap((d) =>
    d.subDomains.flatMap((sd) =>
      sd.controls.map((c) => ({ c, sd, domain: d })),
    ),
  );
  function findControl(id: string) {
    return flatControls.find((x) => x.c.id === id);
  }

  function getThreatDef(threatId: string): ThreatDefinition {
    const meta = THREATS.find((t) => t.id === threatId);
    const mappings = getMappingsForThreat(threatId, FRAMEWORKS).map((m) => ({
      frameworkTitle: m.framework.title,
      frameworkVersion: m.framework.version,
      frameworkId: m.framework.id,
      entryId: m.entry.id,
      shortId: m.entry.shortId,
      entryName: m.entry.name,
      rationale: m.rationale,
    }));
    return {
      threatId,
      name: meta?.name ?? threatId,
      category:
        THREAT_CATEGORIES.find((c) => c.id === meta?.category)?.label ??
        meta?.category ??
        "Operational",
      description: meta?.description ?? "",
      mappings,
    };
  }

  function getControlDef(controlId: string): ControlDefinition | null {
    const f = findControl(controlId);
    if (!f) return null;
    const criteriaMap = f.c.maturityCriteria ?? {};
    const levels = [1, 2, 3, 4, 5].map((level) => ({
      level,
      criteria: criteriaMap[level] ?? "(no criteria authored at this level)",
    }));
    return {
      controlId,
      name: f.c.name,
      domainId: f.domain.id,
      domainName: f.domain.name,
      description: f.c.description ?? "",
      levels,
      mappedStandards: f.c.mappedStandards ?? [],
    };
  }

  // Top threats: take the consolidated top 5, decorate with framework badges.
  const topThreats = profile.consolidated.topThreats.slice(0, 5).map((t) => {
    const def = getThreatDef(t.threatId);
    return {
      threatId: t.threatId,
      name: def.name,
      exposureScore: score100(t.exposure),
      likelihoodPct: Math.round(t.likelihood * 100),
      impactPct: Math.round(t.impact * 100),
      severity: t.severity,
      frameworkBadges: def.mappings,
    };
  });

  // Recommendations: full detail with threats addressed + action steps.
  const recommendations: RecommendationDetail[] = profile.consolidated.recommendations
    .slice(0, 8)
    .map((r, i) => {
      const ctrl = findControl(r.controlId);
      const threatsAddressed = THREATS.filter((t) =>
        t.primaryControlIds.includes(r.controlId) ||
        t.secondaryControlIds.includes(r.controlId),
      )
        .filter((t) =>
          profile.consolidated.threatExposures.some(
            (te) => te.threatId === t.id && te.exposure > 0.1,
          ),
        )
        .slice(0, 6)
        .map((t) => getThreatDef(t.id));
      const actionSteps = ctrl?.c.maturityCriteria?.[r.target]
        ? splitToSteps(ctrl.c.maturityCriteria[r.target] as string)
        : [];
      const frameworkMappings: ThreatDefinition["mappings"] = [];
      const seen = new Set<string>();
      for (const t of threatsAddressed) {
        for (const m of t.mappings) {
          const key = `${m.frameworkId}:${m.entryId}`;
          if (!seen.has(key)) {
            frameworkMappings.push(m);
            seen.add(key);
          }
        }
      }
      const domainLabel = formatDomain(r.domainId, ctrl?.domain.name);
      const targetLabel = formatMaturityLevel(r.target);
      return {
        rank: i + 1,
        controlId: r.controlId,
        controlName: r.controlName,
        domainId: r.domainId,
        domainName: ctrl?.domain.name ?? r.domainId,
        domainLabel,
        current: r.current,
        target: r.target,
        currentLabel: formatMaturityLevel(r.current),
        targetLabel,
        expectedScoreDrop: Math.round(r.expectedScoreDrop),
        threatsAddressed,
        actionSteps,
        mappedStandards: r.mappedStandards ?? [],
        frameworkMappings,
        roleActions: getRoleActions({
          controlId: r.controlId,
          controlName: r.controlName,
          domainLabel,
          targetLabel,
          rank: i + 1,
          firstActionStep: actionSteps[0],
        }),
      };
    });

  // Selected technology domains (overview) + per-domain appendix.
  const selectedUseCases: PerUseCaseSummary[] = input.useCases.map((u) => {
    const meta = USE_CASES.find((x) => x.id === u.id);
    const pp = profile.perUseCase.find((p) => p.useCaseId === u.id);
    const topGapInfo = (() => {
      if (!pp) return undefined;
      const sorted = [...pp.profile.domainScores]
        .sort((a, b) => b.target - b.current - (a.target - a.current));
      const d = sorted[0];
      if (!d) return undefined;
      const dom = framework.domains.find((x) => x.id === d.domainId);
      return {
        controlId: d.domainId,
        domainName: dom?.name ?? d.domainId,
        current: d.current,
        target: d.target,
      };
    })();
    return {
      useCaseId: u.id,
      useCaseName:
        u.id === "other_non_cisco" && u.customDescription
          ? `Other: ${u.customDescription}`
          : meta?.name ?? u.id,
      custom: u.id === "other_non_cisco",
      customDescription: u.customDescription,
      overallScore: pp ? score100(pp.profile.overall) : 0,
      overallLabel: pp?.profile.overallLabel ?? "Low",
      topThreats: (pp?.profile.topThreats ?? []).slice(0, 5).map((t) => ({
        threatId: t.threatId,
        name: THREATS.find((x) => x.id === t.threatId)?.name ?? t.threatId,
        exposureScore: score100(t.exposure),
        likelihoodPct: Math.round(t.likelihood * 100),
        impactPct: Math.round(t.impact * 100),
        severity: t.severity,
      })),
      topGap: topGapInfo,
    };
  });

  // Threat definitions: every threat that appears in topThreats or
  // recommendations.threatsAddressed.
  const threatDefIds = new Set<string>();
  for (const t of topThreats) threatDefIds.add(t.threatId);
  for (const r of recommendations)
    for (const t of r.threatsAddressed) threatDefIds.add(t.threatId);
  const threatDefinitions = Array.from(threatDefIds).map(getThreatDef);

  // Control definitions: every control referenced by recommendations.
  const controlDefIds = new Set<string>();
  for (const r of recommendations) controlDefIds.add(r.controlId);
  const controlDefinitions = Array.from(controlDefIds)
    .map(getControlDef)
    .filter((c): c is ControlDefinition => c !== null);

  // Frameworks referenced: surface every active framework + how many threats
  // it actually maps among those we defined.
  const activeFrameworks = FRAMEWORKS.filter(
    (f) => f.definition.entries.length > 0,
  );
  const frameworksReferenced = activeFrameworks.map((f) => {
    const mappedThreatIds = new Set<string>();
    for (const line of f.mapping.lines) mappedThreatIds.add(line.threatId);
    const mappingCount = Array.from(threatDefIds).filter((tid) =>
      mappedThreatIds.has(tid),
    ).length;
    return {
      id: f.definition.id,
      title: f.definition.title,
      version: f.definition.version,
      publisher: f.definition.publisher,
      url: f.definition.url,
      description: f.definition.description,
      entryCount: f.definition.entries.length,
      mappingCount,
    };
  });

  // V5 — Framework Coverage matrix. Rows = EVERY threat, grouped by threat
  // category (ordered), sorted by exposure within each group. Columns = active
  // frameworks. Renderers draw a compact "✓ N / —" chip from cells[].entries.
  const exposureByThreatId = new Map(
    profile.consolidated.threatExposures.map((t) => [t.threatId, t]),
  );
  const orderedCatIds = [...THREAT_CATEGORIES]
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .map((c) => c.id);
  const catLabelOf = (id: string) =>
    THREAT_CATEGORIES.find((c) => c.id === id)?.label ?? id;
  const catRank = (id: string) => {
    const i = orderedCatIds.indexOf(id);
    return i === -1 ? orderedCatIds.length : i;
  };
  const coverageRows: CoverageRow[] = [...THREATS]
    .sort((a, b) => {
      const byCat = catRank(a.category) - catRank(b.category);
      if (byCat !== 0) return byCat;
      return (
        (exposureByThreatId.get(b.id)?.exposure ?? -1) -
        (exposureByThreatId.get(a.id)?.exposure ?? -1)
      );
    })
    .map((threat) => {
      const t = exposureByThreatId.get(threat.id);
      const cells = activeFrameworks.map((f) => {
        const line = f.mapping.lines.find((l) => l.threatId === threat.id);
        const entries = (line?.entryIds ?? [])
          .map((eid) => {
            const entry = f.definition.entries.find((e) => e.id === eid);
            if (!entry) return null;
            return {
              shortId: entry.shortId,
              entryId: entry.id,
              entryName: entry.name,
              rationale: line?.rationale,
            };
          })
          .filter((e): e is NonNullable<typeof e> => e !== null);
        return { frameworkId: f.definition.id, entries };
      });
      return {
        threatId: threat.id,
        name: threat.name,
        category: catLabelOf(threat.category),
        exposureScore: score100(t?.exposure ?? 0),
        likelihoodPct: Math.round((t?.likelihood ?? 0) * 100),
        impactPct: Math.round((t?.impact ?? 0) * 100),
        severity: t?.severity ?? "Low",
        cells,
      };
    });

  const totalThreats = THREATS.length;
  const coverageTotals = activeFrameworks.map((f) => {
    const mappedThreatIds = new Set<string>();
    for (const line of f.mapping.lines) {
      if (line.entryIds.length > 0) mappedThreatIds.add(line.threatId);
    }
    return {
      frameworkId: f.definition.id,
      mappedThreats: mappedThreatIds.size,
      totalThreats,
    };
  });

  const frameworkCoverage: FrameworkCoverage = {
    frameworks: activeFrameworks.map((f) => ({
      id: f.definition.id,
      title: f.definition.title,
      version: f.definition.version,
    })),
    rows: coverageRows,
    totals: coverageTotals,
  };

  const overall = {
    score: score100(profile.consolidated.overall),
    label: profile.consolidated.overallLabel,
    criticalCount: profile.consolidated.topThreats.filter(
      (t) => t.severity === "Critical",
    ).length,
    highCount: profile.consolidated.topThreats.filter(
      (t) => t.severity === "High",
    ).length,
    useCaseCount: input.useCases.length,
  };

  const domainMaturity = profile.consolidated.domainScores
    .map((d) => {
      const dom = framework.domains.find((x) => x.id === d.domainId);
      return {
        domainId: d.domainId,
        domainName: dom?.name ?? d.domainId,
        domainLabel: formatDomain(d.domainId, dom?.name),
        current: d.current,
        target: d.target,
        currentLabel: formatMaturityLevel(d.current),
        targetLabel: formatMaturityLevel(d.target),
      };
    });

  const focusedSet = new Set(focusedRecommendationIds ?? []);
  const focusedPlan = recommendations.filter((r) => focusedSet.has(r.controlId));

  const productExposure = buildProductExposure(recommendations, CISCO_MAPPINGS);

  // Business-entity inventory, grouped by category — display-only (see
  // modules/entities' v1 scope note), never mind if the caller omits it.
  const entityByCategory = new Map<string, Array<{ label: string; eolRisk: boolean; scored: boolean }>>();
  for (const e of entitiesArg ?? []) {
    const item = getEntityItem(e.itemId);
    if (!item) continue;
    const list = entityByCategory.get(item.categoryId) ?? [];
    list.push({ label: item.label, eolRisk: !!item.eolRisk, scored: resolveUseCaseId(item) !== null });
    entityByCategory.set(item.categoryId, list);
  }
  const selectedEntities = [...entityByCategory.entries()].map(([categoryId, items]) => ({
    categoryLabel: getEntityCategory(categoryId)?.label ?? categoryId,
    items,
  }));

  // Architecture. A model the user edited on the results page always wins;
  // otherwise derive one so a report generated straight from an upload still
  // carries a picture. Both cases record whether it was confirmed, because a
  // client-facing deliverable must not present a guess as a survey.
  const entityList = entitiesArg ?? [];
  const architectureModel =
    topologyArg ?? (entityList.length > 0 ? buildTopology({ entities: entityList, useCases: input.useCases }) : null);
  const architecture = architectureModel ? summariseTopology(architectureModel) : null;

  const executiveSummary = buildExecutiveSummary(
    profile,
    input.useCases.length,
    framework,
    branding.orgName,
    branding.industryProfile,
  );
  const threatMatrix = buildThreatMatrix(
    profile,
    input,
    framework,
    profile.consolidationMode,
  );

  return {
    branding,
    generatedAt: profile.generatedAt,
    consolidationMode: profile.consolidationMode,
    executiveSummary,
    threatMatrix,
    overall,
    selectedUseCases,
    topThreats,
    domainMaturity,
    recommendations,
    focusedPlan,
    perUseCaseAppendix: selectedUseCases,
    threatDefinitions,
    controlDefinitions,
    frameworksReferenced,
    frameworkCoverage,
    ciscoMappings: CISCO_MAPPINGS,
    productExposure,
    selectedEntities,
    architecture,
    architectureModel,
    scopeNote: siteContent.scope.note,
    detailLevel,
  };
}

// Split a multi-clause criteria sentence into bullet-ready action steps.
function splitToSteps(text: string): string[] {
  // Break on semicolons or periods (but not inside parentheses).
  const raw = text
    .split(/[;.]\s+|\.\s*$/)
    .map((s) => s.trim())
    .filter((s) => s.length > 4);
  return raw.length > 0 ? raw : [text];
}
